/**
 * Login Page Authentication System
 *
 * Handles all login-related functionality:
 * - Enhanced authentication with validation
 * - Failed attempt tracking and lockout
 * - CAPTCHA after multiple failures
 * - Google OAuth integration
 * - Session management
 */

// Enhanced Authentication System with Advanced Validation
class EnhancedAuthSystem {
    constructor() {
        this.failedAttempts = parseInt(localStorage.getItem('loginFailedAttempts') || '0');
        this.lastFailedAttempt = localStorage.getItem('lastFailedAttempt');
        this.maxFailedAttempts = 3;
        this.lockoutDuration = 15 * 60 * 1000; // 15 minutes
        this.captcha = this.generateCaptcha();
        // DOM-dependent init is deferred to init() called from DOMContentLoaded
    }

    init() {
        this.initializeValidation();
        this.checkLockoutStatus();
    }

    generateCaptcha() {
        let num1 = Math.floor(Math.random() * 50) + 1;
        let num2 = Math.floor(Math.random() * 50) + 1;
        const operators = ['+', '-', '\u00d7'];
        const operator = operators[Math.floor(Math.random() * operators.length)];

        let answer;
        switch(operator) {
            case '+':
                answer = num1 + num2;
                break;
            case '-':
                // Ensure positive result
                if (num1 < num2) [num1, num2] = [num2, num1];
                answer = num1 - num2;
                break;
            case '\u00d7':
                // Keep multiplication manageable
                num1 = Math.floor(Math.random() * 12) + 2;
                num2 = Math.floor(Math.random() * 12) + 2;
                answer = num1 * num2;
                break;
        }

        return {
            question: `${num1} ${operator} ${num2} = ?`,
            answer: answer
        };
    }

    checkLockoutStatus() {
        if (this.failedAttempts >= this.maxFailedAttempts) {
            const timeSinceLastAttempt = Date.now() - new Date(this.lastFailedAttempt).getTime();

            if (timeSinceLastAttempt < this.lockoutDuration) {
                const remainingTime = Math.ceil((this.lockoutDuration - timeSinceLastAttempt) / 60000);
                this.showLockoutMessage(remainingTime);
                return false;
            } else {
                this.resetFailedAttempts();
            }
        }

        if (this.failedAttempts >= 2) {
            this.showFailedAttemptsWarning();
            this.showCaptcha();
        }

        return true;
    }

    showLockoutMessage(remainingMinutes) {
        showNotification(
            `Account temporarily locked due to multiple failed attempts. Please try again in ${remainingMinutes} minutes.`,
            'error'
        );

        const loginBtn = document.getElementById('loginBtn');
        loginBtn.disabled = true;
        loginBtn.innerHTML = `🔒 Account Locked (${remainingMinutes}m remaining)`;

        // Update countdown every minute
        const countdown = setInterval(() => {
            remainingMinutes--;
            if (remainingMinutes <= 0) {
                clearInterval(countdown);
                this.resetFailedAttempts();
                location.reload();
            } else {
                loginBtn.innerHTML = `🔒 Account Locked (${remainingMinutes}m remaining)`;
            }
        }, 60000);
    }

    showFailedAttemptsWarning() {
        const warning = document.getElementById('failedAttemptsWarning');
        warning.classList.add('show');
    }

    showCaptcha() {
        const container = document.getElementById('captchaContainer');
        const question = document.getElementById('captchaQuestion');

        // Only generate new CAPTCHA if not already showing
        if (!container.classList.contains('show')) {
            this.captcha = this.generateCaptcha();
            question.textContent = this.captcha.question;
            container.classList.add('show');
        }
    }

    validateCaptcha() {
        if (this.failedAttempts < 2) return true;

        const answerInput = document.getElementById('captchaAnswer');
        if (!answerInput || !answerInput.value.trim()) {
            console.warn('❌ CAPTCHA validation failed: Empty input');
            return false;
        }

        const userAnswerInput = answerInput.value.trim();
        const userAnswer = parseInt(userAnswerInput);

        // Check for NaN
        if (isNaN(userAnswer)) {
            console.warn('❌ CAPTCHA validation failed: Invalid number');
            return false;
        }

        const isValid = userAnswer === this.captcha.answer;

        return isValid;
    }

    refreshCaptcha() {
        const question = document.getElementById('captchaQuestion');
        const answerInput = document.getElementById('captchaAnswer');

        this.captcha = this.generateCaptcha();
        question.textContent = this.captcha.question;
        if (answerInput) {
            answerInput.value = '';
        }

        // CAPTCHA refreshed
    }

    recordFailedAttempt() {
        this.failedAttempts++;
        localStorage.setItem('loginFailedAttempts', this.failedAttempts.toString());
        localStorage.setItem('lastFailedAttempt', new Date().toISOString());

        if (this.failedAttempts >= 2) {
            this.showFailedAttemptsWarning();
            if (this.failedAttempts === 2) {
                this.showCaptcha();  // Show for first time
            } else {
                this.refreshCaptcha();  // Refresh with new question
            }
        }
    }

    resetFailedAttempts() {
        this.failedAttempts = 0;
        localStorage.removeItem('loginFailedAttempts');
        localStorage.removeItem('lastFailedAttempt');

        const warning = document.getElementById('failedAttemptsWarning');
        warning.classList.remove('show');

        const captcha = document.getElementById('captchaContainer');
        captcha.classList.remove('show');
    }

    async login(emailPhone, password, rememberMe = false) {
        // Check lockout status
        if (!this.checkLockoutStatus()) {
            throw new Error('Account is temporarily locked');
        }

        // Validate CAPTCHA if required
        if (this.failedAttempts >= 2 && !this.validateCaptcha()) {
            throw new Error('Please solve the CAPTCHA correctly');
        }

        try {
            // Wait for Supabase to be ready
            const api = await window.SupabaseReady;
            if (!api) {
                throw new Error('Authentication service is not available. Please try again later.');
            }

            // Use Supabase Auth for customer login
            const result = await api.auth.signIn({
                email: emailPhone,
                password: password
            });

            if (result.success) {
                this.resetFailedAttempts();

                const loginUser = {
                    id: result.user.id,
                    name: result.user.user_metadata?.full_name || result.user.email,
                    email: result.user.email,
                    phone: result.user.user_metadata?.phone || '',
                    loginTime: new Date().toISOString(),
                    rememberMe
                    // Tokens are managed by Supabase SDK session, not stored in localStorage
                };

                this.saveCurrentUser(loginUser);

                return { success: true, user: loginUser };
            } else {
                // Login failed
                this.recordFailedAttempt();
                return {
                    success: false,
                    message: result.error || 'Invalid email or password.'
                };
            }
        } catch (error) {
            console.error('Login error:', error);
            this.recordFailedAttempt();

            let message = error.message || 'Invalid email/phone or password. Please try again.';

            return { success: false, message };
        }
    }

    generateSessionId() {
        return 'sess_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
    }

    saveCurrentUser(user) {
        const userData = {
            ...user,
            expiresAt: user.rememberMe
                ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000) // 30 days
                : new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) // 7 days
        };
        localStorage.setItem('burgerPrimeCurrentUser', JSON.stringify(userData));
    }

    initializeValidation() {
        const inputs = document.querySelectorAll('[data-validation]');
        inputs.forEach(input => {
            input.addEventListener('blur', () => this.validateField(input));
            input.addEventListener('input', () => this.clearValidation(input));
        });
    }

    validateField(input) {
        const validationType = input.dataset.validation;
        const value = input.value.trim();
        let isValid = false;
        let message = '';

        switch(validationType) {
            case 'emailOrPhone':
                isValid = this.validateEmailOrPhone(value);
                message = isValid ? 'Valid' : 'Please enter a valid email or phone number';
                break;
            case 'password':
                isValid = value.length >= 6;
                message = isValid ? 'Valid' : 'Password must be at least 6 characters';
                break;
        }

        this.showValidationResult(input, isValid, message);
        return isValid;
    }

    validateEmailOrPhone(value) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        const phoneRegex = /^[6-9]\d{9}$/;

        return emailRegex.test(value) || phoneRegex.test(value.replace(/\D/g, ''));
    }

    showValidationResult(input, isValid, message) {
        const errorElement = document.getElementById(input.getAttribute('aria-describedby'));

        if (isValid) {
            input.classList.remove('invalid');
            input.classList.add('valid');
            errorElement.textContent = message;
            errorElement.className = 'validation-message success show';
        } else {
            input.classList.remove('valid');
            input.classList.add('invalid');
            errorElement.textContent = message;
            errorElement.className = 'validation-message error show';
        }
    }

    clearValidation(input) {
        const errorElement = document.getElementById(input.getAttribute('aria-describedby'));
        input.classList.remove('valid', 'invalid');
        errorElement.classList.remove('show');
    }

    validateForm() {
        const emailPhone = document.getElementById('loginEmailPhone');
        const password = document.getElementById('loginPassword');

        const isEmailPhoneValid = this.validateField(emailPhone);
        const isPasswordValid = this.validateField(password);

        return isEmailPhoneValid && isPasswordValid;
    }
}

// Auth system instance — created immediately but DOM init deferred
let authSystem;

// Enhanced login form handler
async function handleLogin(event) {
    event.preventDefault();

    // Validate form
    if (!authSystem.validateForm()) {
        showNotification('Please fix the errors above', 'error');
        return;
    }

    const emailPhone = document.getElementById('loginEmailPhone').value.trim();
    const password = document.getElementById('loginPassword').value;
    const rememberMe = document.getElementById('rememberMe').checked;

    const loginBtn = document.getElementById('loginBtnText');
    const originalText = loginBtn.textContent;
    loginBtn.innerHTML = '<span class="loading"></span>Signing you in...';
    document.getElementById('loginBtn').disabled = true;

    try {
        const result = await authSystem.login(emailPhone, password, rememberMe);

        if (result.success) {
            showNotification(`Welcome back, ${result.user.name}!`, 'success');

            // Show success state
            loginBtn.innerHTML = '✅ Login Successful';

            // Redirect to main page after 2 seconds
            setTimeout(() => {
                // Try multiple redirect methods
                try {
                    // Method 1: Use window.location.href with absolute path
                    const baseUrl = window.location.origin;
                    window.location.href = `${baseUrl}/index.html`;
                } catch (error) {
                    // Method 2: Fallback to relative redirect
                    try {
                        window.location.replace('./index.html');
                    } catch (error2) {
                        // Method 3: Last resort - force page reload with home
                        window.location.href = '/';
                    }
                }
            }, 2000);
        } else {
            showNotification(result.message, 'error');
        }
    } catch (error) {
        console.error('Login error:', error);
        showNotification(error.message || 'Login failed. Please try again.', 'error');
    } finally {
        if (!document.getElementById('loginBtn').innerHTML.includes('✅')) {
            loginBtn.textContent = originalText;
            document.getElementById('loginBtn').disabled = false;
        }
    }
}

// Enhanced social login functions with OAuth
async function loginWithGoogle() {
    try {
        console.log('🔐 Initiating Google login...');

        const api = await window.SupabaseReady;
        if (!api) {
            throw new Error('Authentication service is not available.');
        }

        showNotification('Redirecting to Google...', 'info');

        const result = await api.auth.signInWithOAuth({
            provider: 'google',
            redirectTo: `${window.location.origin}/index.html`
        });

        if (!result.success) {
            throw new Error(result.error);
        }

        // The redirect happens automatically
        // User will be redirected to Google, then back to your site
        console.log('✅ Redirecting to Google for authentication...');

    } catch (error) {
        console.error('Google login error:', error);
        showNotification('Google login failed: ' + error.message, 'error');
    }
}


// Enhanced password toggle functionality
function togglePassword(inputId) {
    const input = document.getElementById(inputId);
    const toggle = input.parentElement.querySelector('.password-toggle');
    const icon = toggle.querySelector('#toggleIcon');
    const text = toggle.querySelector('#toggleText');

    if (input.type === 'password') {
        input.type = 'text';
        icon.textContent = '🙈';
        text.textContent = 'Hide';
        toggle.setAttribute('aria-label', 'Hide password');
        // Add a brief highlight effect
        toggle.style.background = 'var(--primary-crimson)';
        toggle.style.color = 'white';
        setTimeout(() => {
            toggle.style.background = 'rgba(220, 20, 60, 0.1)';
            toggle.style.color = 'var(--primary-crimson)';
        }, 200);
    } else {
        input.type = 'password';
        icon.textContent = '👁️';
        text.textContent = 'Show';
        toggle.setAttribute('aria-label', 'Show password');
    }
}

// Enhanced notification system
function showNotification(message, type = 'info') {
    // Remove existing notifications
    const existingNotifications = document.querySelectorAll('.notification');
    existingNotifications.forEach(notification => notification.remove());

    const notification = document.createElement('div');
    notification.className = `notification ${type}`;
    const iconSpan = document.createElement('span');
    iconSpan.textContent = getNotificationIcon(type);
    const msgSpan = document.createElement('span');
    msgSpan.textContent = message;
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'display: flex; align-items: center; gap: 10px;';
    wrapper.appendChild(iconSpan);
    wrapper.appendChild(msgSpan);
    notification.appendChild(wrapper);

    document.body.appendChild(notification);

    // Auto remove after 5 seconds
    setTimeout(() => {
        notification.style.animation = 'slideOutRight 0.3s ease-in';
        setTimeout(() => notification.remove(), 300);
    }, 5000);
}

function getNotificationIcon(type) {
    const icons = {
        success: '✅',
        error: '❌',
        warning: '⚠️',
        info: 'ℹ️'
    };
    return icons[type] || 'ℹ️';
}

// Enhanced initialization
document.addEventListener('DOMContentLoaded', function() {
    // Initialize auth system now that DOM is ready
    authSystem = new EnhancedAuthSystem();
    authSystem.init();

    // Wire up form submission
    const loginForm = document.getElementById('loginForm');
    if (loginForm) {
        loginForm.addEventListener('submit', handleLogin);
    }

    // Wire up Google login button
    const googleBtn = document.getElementById('googleLoginBtn');
    if (googleBtn) {
        googleBtn.addEventListener('click', loginWithGoogle);
    }

    // Wire up password toggle
    const passwordToggle = document.querySelector('.password-toggle');
    if (passwordToggle) {
        passwordToggle.addEventListener('click', function() {
            togglePassword('loginPassword');
        });
    }

    // Wire up CAPTCHA refresh button
    const captchaRefresh = document.getElementById('refreshCaptcha');
    if (captchaRefresh) {
        captchaRefresh.addEventListener('click', function() {
            authSystem.refreshCaptcha();
        });
    }

    // Auto-focus first input
    const emailInput = document.getElementById('loginEmailPhone');
    if (emailInput) emailInput.focus();

    // Check URL params to determine why the user landed here
    const urlParams = new URLSearchParams(window.location.search);
    const reason = urlParams.get('reason');

    // Check if user is already logged in
    const currentUser = localStorage.getItem('burgerPrimeCurrentUser');
    if (currentUser) {
        try {
            const userData = JSON.parse(currentUser);
            const isValid = !userData.expiresAt || new Date(userData.expiresAt) > new Date();

            if (isValid && reason !== 'signin') {
                // Valid session — auto-redirect back to home
                showNotification('You are already logged in. Redirecting...', 'info');
                setTimeout(() => {
                    window.location.href = 'index.html';
                }, 1500);
            } else if (!isValid) {
                // Session truly expired — clean up and show message only if they came here for a reason
                localStorage.removeItem('burgerPrimeCurrentUser');
                if (reason === 'signin' || reason === 'expired') {
                    showNotification('Your session has expired. Please log in again.', 'warning');
                }
            }
        } catch (e) {
            localStorage.removeItem('burgerPrimeCurrentUser');
        }
    } else if (reason === 'signin') {
        showNotification('Please sign in to continue.', 'info');
    }

    // Add input event listeners for real-time validation
    const inputs = document.querySelectorAll('.form-input');
    inputs.forEach(input => {
        input.addEventListener('input', function() {
            if (this.value.length > 0) {
                this.classList.add('has-content');
            } else {
                this.classList.remove('has-content');
            }
        });
    });

    // Add keyboard shortcuts
    document.addEventListener('keydown', function(e) {
        // Submit on Ctrl+Enter
        if (e.ctrlKey && e.key === 'Enter') {
            e.preventDefault();
            document.getElementById('loginForm').dispatchEvent(new Event('submit'));
        }

        // Clear form on Ctrl+R (prevent default refresh)
        if (e.ctrlKey && e.key === 'r') {
            e.preventDefault();
            if (confirm('Clear form data?')) {
                document.getElementById('loginForm').reset();
                authSystem.resetFailedAttempts();
                location.reload();
            }
        }
    });

    // Add form auto-save for email/phone (not password for security)
    const emailPhoneInput = document.getElementById('loginEmailPhone');
    const savedEmailPhone = localStorage.getItem('burgerPrime_lastEmailPhone');
    if (savedEmailPhone) {
        emailPhoneInput.value = savedEmailPhone;
    }

    emailPhoneInput.addEventListener('blur', function() {
        if (this.value.trim()) {
            localStorage.setItem('burgerPrime_lastEmailPhone', this.value.trim());
        }
    });
});

// Enhanced error handling
window.addEventListener('error', function(e) {
    console.error('Unexpected error:', e.error);
    showNotification('An unexpected error occurred. Please refresh the page.', 'error');
});

// Network status monitoring
window.addEventListener('online', function() {
    showNotification('Connection restored', 'success');
});

window.addEventListener('offline', function() {
    showNotification('You are offline. Some features may not work.', 'warning');
});

// Security event logging
function logSecurityEvent(eventType, details = {}) {
    const logEntry = {
        timestamp: new Date().toISOString(),
        event: eventType,
        userAgent: navigator.userAgent,
        url: window.location.href,
        ...details
    };

    const securityLog = JSON.parse(localStorage.getItem('burgerPrime_securityLog') || '[]');
    securityLog.push(logEntry);

    // Keep only last 100 entries
    if (securityLog.length > 100) {
        securityLog.splice(0, securityLog.length - 100);
    }

    localStorage.setItem('burgerPrime_securityLog', JSON.stringify(securityLog));
}

// Log page load
logSecurityEvent('login_page_loaded');
